"use client";

import Image from "next/image";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";

interface CommunityImageData {
  src: string;
  alt: string;
  title: string;
}

interface CommunityMarqueeProps {
  images: CommunityImageData[];
  cms?: Record<string, string>;
}

export default function CommunityMarquee({ images, cms }: CommunityMarqueeProps) {
  const [paused, setPaused] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const resumeTimeoutRef = useRef<number | null>(null);

  const clearResumeTimer = () => {
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  };

  const pauseForInteraction = () => {
    clearResumeTimer();
    setPaused(true);
  };

  const scheduleResume = (delay = 3000) => {
    clearResumeTimer();
    // window.setTimeout returns a number in browser
    resumeTimeoutRef.current = window.setTimeout(() => {
      resumeTimeoutRef.current = null;
      setPaused(false);
    }, delay) as unknown as number;
  };

  useEffect(() => {
    setIsMounted(true);
    return () => {
      clearResumeTimer();
    };
  }, []);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-14 max-w-4xl text-left ml-0 pl-0">
          {cms?.heading && (
            <motion.h2
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-school-navy mb-8"
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-100px 0px -100px 0px" }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
            >
              {cms.heading}
            </motion.h2>
          )}
          {cms?.description && (
            <div className="text-lg sm:text-xl md:text-2xl text-school-navy space-y-6 font-light leading-relaxed">
              <p>{cms.description}</p>
            </div>
          )}
        </div>
        {!isMounted ? (
          // Server-side fallback: static grid
          <div className="mb-8 overflow-hidden">
            <div className="flex gap-4">
              {images.slice(0, 6).map((img, idx) => (
                <div key={idx} className="shrink-0">
                  <div className="w-45 sm:w-55 md:w-65 lg:w-80 aspect-square relative">
                    <Image
                      src={img.src}
                      alt={img.alt}
                      fill
                      className="rounded-lg object-cover"
                      sizes="(max-width: 639px) 180px, (max-width: 1023px) 220px, (max-width: 1279px) 260px, 320px"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="mb-8 relative"
            onPointerDown={pauseForInteraction}
            onPointerUp={() => scheduleResume()}
            onTouchStart={pauseForInteraction}
            onTouchEnd={() => scheduleResume()}
            onMouseEnter={pauseForInteraction}
            onMouseLeave={() => scheduleResume()}
            onFocus={pauseForInteraction}
            onBlur={() => scheduleResume()}
          >
            <Marquee
              pauseOnHover
              gradient={false}
              speed={40}
              play={!paused}
              className="flex"
            >
              {images.map((img, idx) => (
                <div key={idx} className="shrink-0">
                  <div className="w-45 sm:w-55 md:w-65 lg:w-80 aspect-square relative mx-4">
                    <Image
                      src={img.src}
                      alt={img.alt}
                      fill
                      className="rounded-lg object-cover"
                      sizes="(max-width: 639px) 180px, (max-width: 1023px) 220px, (max-width: 1279px) 260px, 320px"
                    />
                  </div>
                </div>
              ))}
            </Marquee>
            
            {/* Interaction-based pause: touch/drag/hover/focus will pause. After 3s of inactivity the marquee resumes. No overlay button on mobile. */}
          </div>
        )}
        <div className="text-center">
          <a
            href="/contact-us"
            className="bg-school-gold hover:bg-school-gold-dark text-black px-8 py-3 font-bold uppercase tracking-wide transition-colors inline-block rounded text-base sm:text-lg"
          >
            CONTACT US
          </a>
        </div>
      </div>
    </section>
  );
}
